import { initialState, relativeTimestamp } from '@/Helpers.js';
import Model from '@/models/Model.js';
import ReadReceipt from '@/models/ReadReceipt.js';
import axios from '@/plugins/axios.js';
import moment from '@/plugins/vue-moment.js';

export default class CourseAnnouncement extends Model {
    static entity = 'course_announcements';

    static modelName = 'CourseAnnouncement';

    static fields() {
        return {
            id: this.attr(null),
            body: this.attr(null),
            safe_body: this.attr(null),
            course_id: this.attr(null),
            media: this.attr(null),
            title: this.attr(null),
            published_at: this.attr(null),
            alertType: this.string('Course Announcement'),
            important: this.attr(null),
            read: this.attr(null),

            ...super.fields(),

            creator: this.belongsTo('users', 'created_by'),
            course: this.belongsTo('courses', 'course_id'),
            read_receipts: this.morphMany('read_receipts', 'readable_id', 'readable_type_entity'),
        };
    }

    get marked_as_read() {
        const userId = Model.store().getters['Auth/userId'];

        // Read state is driven purely by read receipts so the author can toggle
        // their own announcement between read and unread, matching the backend
        // (which never auto-marks an announcement read for its creator).
        const modelType = 'App\\Models\\CourseAnnouncement';

        return ReadReceipt.query()
            .where('readable_id', this.id)
            .where('readable_type', modelType)
            .where('user_id', userId)
            .exists();
    }

    get published_label() {
        return relativeTimestamp(this.timestamp('published_at'), { pastLabel: '', futureLabel: '', longFormat: false });
    }

    get created_label() {
        return relativeTimestamp(this.timestamp('created_at'), { pastLabel: '', futureLabel: '', longFormat: false });
    }

    get router_link() {
        return {
            name: 'course-dashboard',
            params: { courseId: this.course_id },
        };
    }

    get backend_class() {
        return 'App\\Models\\CourseAnnouncement';
    }

    static async fetch(endpoint) {
        const { response } = await this.api().get(endpoint, {
            persistBy: 'create',
        });

        return response.data;
    }

    markAsRead() {
        return ReadReceipt.api().put('/api/mark-as-read', {
            readable_id: this.id,
            readable_type: 'App\\Models\\CourseAnnouncement',
        });
    }

    async markAsUnread() {
        const response = await axios.put('/api/mark-as-unread', {
            readable_id: this.id,
            readable_type: 'App\\Models\\CourseAnnouncement',
        });

        ReadReceipt.query()
            .where('readable_id', this.id)
            .where('readable_type', 'App\\Models\\CourseAnnouncement')
            .where('user_id', CourseAnnouncement.store().getters['Auth/userId'])
            .get()
            .forEach(readReceipt => readReceipt.$delete());

        return response;
    }

    get formatted_created_at() {
        return this.timestamp('created_at').format('MM/DD/YYYY - h:mm a');
    }

    /**
     * Absolute created/publish dates for the viewing modal. Unlike
     * `created_label` / `published_label` (relative, "Scheduled for ..."
     * phrasing intended for the widget subtitle), these read as plain dates
     * so the modal shows "Created: Apr 2, 2026" rather than
     * "Created: Scheduled for ...".
     *
     * These resolve the timezone locally instead of using the base
     * `timestamp()` helper: `timestamp()` forwards the institution timezone
     * straight to moment-timezone's `.tz()`, and when that setting is absent
     * `.tz(undefined)` is the *getter* form and returns `undefined`. A bare
     * `.format()` on that then throws and the whole modal fails to render
     * (QA TC-04). Falling back to the same default the vue-moment/$timezone
     * plugins use keeps the modal dates showing a real value.
     */
    absoluteDateLabel(field) {
        const timezone = initialState('InstitutionSettings.timezone') || 'America/Los_Angeles';

        return moment.utc(this[field]).tz(timezone).format('MMM D, YYYY [at] h:mm A');
    }

    get created_date_label() {
        return this.absoluteDateLabel('created_at');
    }

    get published_date_label() {
        return this.absoluteDateLabel('published_at');
    }
}
